import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Search,
} from 'lucide-react';

const stopGridInteraction = (event) => {
  event.stopPropagation();
};

const formatLabel = (key) =>
  key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');

const ColumnFilter = ({
  uniqueValues,
  selectedValues,
  onChange,
  isOpen,
  onToggle,
  onClose,
  filterActive,
}) => {
  const containerRef = useRef(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, onClose]);

  const filteredValues = useMemo(
    () =>
      uniqueValues.filter((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      ),
    [uniqueValues, search]
  );

  const allFilteredSelected =
    filteredValues.length > 0 &&
    filteredValues.every((value) => selectedValues.has(String(value)));

  const handleSelectAll = () => {
    const next = new Set(selectedValues);
    if (allFilteredSelected) {
      filteredValues.forEach((value) => next.delete(String(value)));
    } else {
      filteredValues.forEach((value) => next.add(String(value)));
    }
    onChange(next);
  };

  const handleToggleValue = (value) => {
    const key = String(value);
    const next = new Set(selectedValues);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange(next);
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-flex no-drag"
      onMouseDown={stopGridInteraction}
      onPointerDown={stopGridInteraction}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`p-1 rounded hover:bg-gray-200 transition-colors ${
          filterActive ? 'text-primary bg-primary/10' : 'text-gray-400'
        }`}
        title="Filter column"
      >
        <Filter size={14} />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 z-[200] bg-white border border-gray-200 rounded-lg shadow-xl min-w-[220px] max-w-[280px]"
          onMouseDown={stopGridInteraction}
          onPointerDown={stopGridInteraction}
          onClick={stopGridInteraction}
        >
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onMouseDown={stopGridInteraction}
                onPointerDown={stopGridInteraction}
                onKeyDown={stopGridInteraction}
                placeholder="Search..."
                autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1 data-table-scroll">
            <label className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={handleSelectAll}
                onMouseDown={stopGridInteraction}
                className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
              />
              <span className="font-medium">Select all</span>
            </label>
            {filteredValues.map((value) => {
              const key = String(value);
              return (
                <label
                  key={key}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.has(key)}
                    onChange={() => handleToggleValue(value)}
                    onMouseDown={stopGridInteraction}
                    className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="truncate">{value === '' || value == null ? '(empty)' : String(value)}</span>
                </label>
              );
            })}
            {filteredValues.length === 0 && (
              <p className="px-2 py-3 text-sm text-gray-400 text-center">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DataTable = ({ data, columns: propColumns, disableFilters = false }) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});

  const columns = useMemo(() => {
    if (propColumns && propColumns.length > 0) return propColumns;
    if (!data || data.length === 0) return [];

    return Object.keys(data[0]).map((key) => ({
      key,
      label: formatLabel(key),
      sortable: true,
      filterable: true,
    }));
  }, [data, propColumns]);

  const uniqueValuesByColumn = useMemo(() => {
    const result = {};
    columns.forEach((column) => {
      const values = new Set();
      (data || []).forEach((row) => {
        values.add(String(row[column.key] ?? ''));
      });
      result[column.key] = Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    });
    return result;
  }, [columns, data]);

  const getColumnFilter = useCallback(
    (columnKey) => {
      if (columnFilters[columnKey]) {
        return columnFilters[columnKey];
      }
      return new Set(uniqueValuesByColumn[columnKey] || []);
    },
    [columnFilters, uniqueValuesByColumn]
  );

  const isColumnFilterActive = useCallback(
    (columnKey) => {
      const selected = getColumnFilter(columnKey);
      const all = uniqueValuesByColumn[columnKey] || [];
      return selected.size < all.length;
    },
    [getColumnFilter, uniqueValuesByColumn]
  );

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((row) =>
      columns.every((column) => {
        if (disableFilters || column.filterable === false) return true;
        const selected = getColumnFilter(column.key);
        const all = uniqueValuesByColumn[column.key] || [];
        if (selected.size === all.length) return true;
        return selected.has(String(row[column.key] ?? ''));
      })
    );
  }, [data, columns, disableFilters, getColumnFilter, uniqueValuesByColumn]);

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal ?? '');
      const bStr = String(bVal ?? '');
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredData, sortColumn, sortDirection]);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp size={14} className="text-primary" />;
    }
    if (sortDirection === 'desc') {
      return <ArrowDown size={14} className="text-primary" />;
    }
    return <ArrowUpDown size={14} className="text-gray-400" />;
  };

  const formatValue = (value) => {
    if (value == null || value === '') return '-';
    if (typeof value === 'number') {
      if (value >= 1000) {
        return new Intl.NumberFormat('en-US').format(value);
      }
      return value.toString();
    }
    return value;
  };

  const handleFilterChange = (columnKey, selectedValues) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: selectedValues,
    }));
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-auto data-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {columns.map((column) => {
                const showFilter = !disableFilters && column.filterable !== false;
                const filterActive = showFilter && isColumnFilterActive(column.key);

                return (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left font-semibold text-gray-700 border-b whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => column.sortable !== false && handleSort(column.key)}
                        className={`flex items-center gap-1.5 ${
                          column.sortable !== false ? 'cursor-pointer hover:text-gray-900' : ''
                        }`}
                      >
                        <span className="uppercase text-xs tracking-wide">{column.label}</span>
                        {column.sortable !== false && getSortIcon(column.key)}
                      </button>
                      {showFilter && (
                        <ColumnFilter
                          uniqueValues={uniqueValuesByColumn[column.key] || []}
                          selectedValues={getColumnFilter(column.key)}
                          onChange={(values) => handleFilterChange(column.key, values)}
                          isOpen={openFilterColumn === column.key}
                          onToggle={() =>
                            setOpenFilterColumn(
                              openFilterColumn === column.key ? null : column.key
                            )
                          }
                          onClose={() => setOpenFilterColumn(null)}
                          filterActive={filterActive}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  No rows match the current filters
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatValue(row[column.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {sortedData.length > 0 && (
        <div className="flex-shrink-0 px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">
          {sortedData.length} {sortedData.length === 1 ? 'row' : 'rows'}
          {sortedData.length !== data.length && ` (filtered from ${data.length})`}
        </div>
      )}
      <style>{`
        .data-table-scroll {
          scrollbar-width: thin;
          scrollbar-color: #d1d5db transparent;
        }
        .data-table-scroll::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }
        .data-table-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .data-table-scroll::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 9999px;
        }
        .data-table-scroll::-webkit-scrollbar-thumb:hover {
          background-color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default DataTable;
